import { moduleTermine } from '../Certificat/moduleTermine'
import { couleurDuPalier } from '../Formats/FormatDiag'
import { FORME_TU } from '../../../utils/formeAdresse'

/* ⛔ BILAN SOFT SKILLS : QUELS TEXTES SUR LA PAGE (07/10/2026, etape 1,
   MAQUETTE : aucun ecran ne s'en sert encore).

   REGLE DE NAWAL : AUCUN texte genere, AUCUNE reponse libre de l'apprenant
   imprimee. La page assemble des textes VALIDES, tires de la bibliotheque des
   18 fiches de competence (`bibliotheque_18_competences.json`), choisis
   d'apres les scores du scan. Pas de pourcentage : les chiffres restent ici,
   la page ne recoit que des textes et des jauges (segments et couleur).

   DEUX PUBLICS (decision de Nawal du 07/10/2026) : « etudiant » (TU, peu
   d'experience professionnelle) et « professionnel » (VOUS, experimente).
   Ils different par le registre ET par le contexte des exemples. Etape 2 :
   le public suit la FORME D'ADRESSE publiee (contexts/FormeAdresseContext,
   d'apres la cible du module en cours) : tu = etudiant, vous =
   professionnel (`publicDeLaForme`). Pas de second mecanisme.

   LA BIBLIOTHEQUE, deux formes lues telles quelles :
     - v2 : champs communs `cle`, `famille`, `nom_coreskills`, `nom_usage`,
       puis `etudiant` et `professionnel`, chacun avec `en_situation`,
       `phrases_cv` (3), `amorce_exemple` et `conseil_consolider` (une chaine :
       TU pour etudiant, VOUS pour professionnel) ;
     - v1 : `au_travail`, `phrases_cv`, `amorce_exemple` {tu, vous},
       `conseil_consolider` {tu, vous} : etudiant prend « tu », professionnel
       « vous », `au_travail` tient lieu d'`en_situation` pour les deux.
   Le fichier peut etre une liste de fiches, `{ competences: [...] }` ou un
   objet range par cle. `"provisoire": true` (en tete ou sur une fiche) : la
   page le signale. Remplacer le fichier par la version validee suffit : ni
   ce fichier ni la mise en page ne changent.

   LES SCORES. Ceux du module de scan, `resultFin` (UserContext.js,
   getFormationResults) : une categorie par competence (`planification`,
   `efficacité`, ...). La cle d'une categorie et celle d'une fiche se comparent
   sans casse, sans accents, sans espaces ni ponctuation (« flexibilité
   mentale » = « flexibilite_mentale »).

   ⛔ MULTITACHE ET AUTONOMIE N'EXISTENT PLUS (decision de Nawal du
   07/10/2026) : d'anciennes competences, remplacees. Le bilan les IGNORE :
   jamais affichees, aucune fiche, ecartees AVANT le choix des atouts et de la
   competence a consolider, meme si une bibliotheque ou un scan les porte
   encore (le scan B1 les note toujours).

   v2 du 07/10 : plus de « regard sur soi » tire des echelles T1/T2 ; la page
   porte un texte fixe, « Ce que tu as gagné », sans aucune donnee. */

export const COMPETENCES_RETIREES = ['multitache', 'autonomie']
export const PUBLICS = ['etudiant', 'professionnel']
const REGISTRE = { etudiant: 'tu', professionnel: 'vous' }

export const aplatirCle = (t) => String(t === null || t === undefined ? '' : t)
  .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '')

export const estRetiree = (cle) => COMPETENCES_RETIREES.includes(aplatirCle(cle))

export const publicDeLaForme = (forme) => (forme === FORME_TU ? 'etudiant' : 'professionnel')

/* Typographie francaise : espace insecable avant « : ; ? ! » et apres « ;
   l'espace fine insecable (absente de Questrial) devient une insecable. */
export const typographier = (t) => String(t === null || t === undefined ? '' : t)
  .replace(/\u202f/g, '\u00a0')
  .replace(/ ([:;?!»])/g, '\u00a0$1')
  .replace(/« /g, '«\u00a0')
  .replace(/\s+$/, '').replace(/^\s+/, '')

const TIRETS_INTERDITS = /[\u2013\u2014]/

const lirePublic = (f, p) => {
  const v2 = f?.[p]
  const source = v2 && typeof v2 === 'object' && !Array.isArray(v2) ? v2 : null
  const chaine = (v) => (typeof v === 'string' ? v : v?.[REGISTRE[p]])
  const depuis = source || f
  return {
    enSituation: typographier(depuis?.en_situation ?? depuis?.au_travail),
    phrasesCv: Array.isArray(depuis?.phrases_cv) ? depuis.phrases_cv.map(typographier) : [],
    amorce: typographier(chaine(depuis?.amorce_exemple)),
    conseil: typographier(chaine(depuis?.conseil_consolider)),
  }
}

/* Ce qui manque a une fiche pour aller sur la page, pour ce public. */
export const manquesDeLaFiche = (fiche, pub = 'etudiant') => {
  const manques = []
  const t = fiche?.publics?.[pub]
  if (!fiche?.nom_coreskills) manques.push('nom_coreskills')
  if (!fiche?.nom_usage) manques.push('nom_usage')
  if (!t?.enSituation) manques.push(pub + '.en_situation')
  if (!Array.isArray(t?.phrasesCv) || t.phrasesCv.length !== 3 || t.phrasesCv.some((p) => !p)) manques.push(pub + '.phrases_cv (3)')
  if (!t?.amorce) manques.push(pub + '.amorce_exemple')
  if (!t?.conseil) manques.push(pub + '.conseil_consolider')
  const textes = [fiche?.nom_coreskills, fiche?.nom_usage, t?.enSituation, ...(t?.phrasesCv || []), t?.amorce, t?.conseil]
  if (textes.some((x) => TIRETS_INTERDITS.test(String(x || '')))) manques.push(pub + '\u00a0: tiret cadratin ou demi-cadratin à retirer')
  return manques
}

export const lireBibliotheque = (json) => {
  let brut = []
  if (Array.isArray(json)) brut = json
  else if (Array.isArray(json?.competences)) brut = json.competences
  else if (Array.isArray(json?.fiches)) brut = json.fiches
  else if (json && typeof json === 'object') {
    brut = Object.entries(json)
      .filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v))
      .map(([cle, v]) => ({ cle, ...v }))
  }
  const provisoireEnTete = !Array.isArray(json) && json?.provisoire === true
  const fiches = []
  const anomalies = []
  brut.forEach((f, i) => {
    if (!aplatirCle(f?.cle)) { anomalies.push('fiche ' + (i + 1) + '\u00a0: pas de clé'); return }
    if (estRetiree(f.cle) || estRetiree(f.nom_coreskills)) return
    const fiche = {
      cle: f.cle,
      famille: typographier(f.famille),
      nom_coreskills: typographier(f.nom_coreskills),
      nom_usage: typographier(f.nom_usage),
      version: PUBLICS.some((p) => f?.[p] && typeof f[p] === 'object') ? 2 : 1,
      provisoire: provisoireEnTete || f.provisoire === true,
      publics: {},
    }
    PUBLICS.forEach((p) => { fiche.publics[p] = lirePublic(f, p) })
    PUBLICS.forEach((p) => {
      const manques = manquesDeLaFiche(fiche, p)
      if (manques.length) anomalies.push(f.cle + '\u00a0: ' + manques.join(', '))
    })
    fiches.push(fiche)
  })
  /* une fiche se retrouve par sa cle ou par son nom CoreSkills */
  const index = {}
  fiches.forEach((f) => { index[aplatirCle(f.cle)] = f })
  fiches.forEach((f) => { const k = aplatirCle(f.nom_coreskills); if (k && !index[k]) index[k] = f })
  return { fiches, index, provisoire: provisoireEnTete, anomalies }
}

export const trouverFiche = (bibliotheque, cle) => (estRetiree(cle) ? null : bibliotheque?.index?.[aplatirCle(cle)] || null)

/* ------------------------------------------------------- les scores du scan */
const nombre = (v) => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export const lireResultFin = (resultFin) => {
  if (typeof resultFin === 'string') {
    try { return JSON.parse(resultFin) } catch (e) { return null }
  }
  return resultFin && typeof resultFin === 'object' ? resultFin : null
}

/* Les scores des competences qui ont une fiche. `sansFiche` : les categories
   notees qui n'ont pas de fiche (ni master, ni glbMaster, ni competence
   retiree) ; elles ne vont pas sur la page. */
export const scoresDuScan = (resultFin, bibliotheque) => {
  const r = lireResultFin(resultFin)
  if (!r) return null
  const scores = {}
  const sansFiche = []
  Object.entries(r).forEach(([cle, v]) => {
    const s = nombre(v?.score)
    if (s === null || cle === 'master' || cle === 'glbMaster' || estRetiree(cle)) return
    if (trouverFiche(bibliotheque, cle)) scores[cle] = s
    else sansFiche.push(cle)
  })
  return { scores, sansFiche }
}

/* ------------------------------------------------------------ la jauge
   Celle des ecrans Diagnostic (FormatDiag.js) : 20 segments, autant de
   remplis que la fraction du score sur 20 (arrondie), tous de la couleur du
   palier atteint (`couleurDuPalier`, echelle RD_YL_GN de FormatDiag) ; le
   reste en #EFEFEF. Les paliers sont les seuils du Diagnostic de la
   competence, du plus haut au plus bas : ceux du scan B1 par defaut (66, 33,
   0). La page recoit des segments et une couleur, JAMAIS le score.
   v4 et v5 du 07/10 (Nawal), SUR LE BILAN SEULEMENT (le Diagnostic de la
   plateforme ne change pas) : avec trois paliers (le cas du scan), BAS en
   rouge brique #B23A3A (deja employe dans l'admin), MILIEU en orange brule
   de la charte #E26438 (a la place du jaune pale #FFFFBF, presque
   invisible), HAUT en vert #91CF60. Ecarts de couleur CIE76 : 25 entre bas
   et milieu, 87 entre milieu et haut ; la clarte monte du bas au haut
   (L* 42, 58, 77), l'ordre se lit d'un coup d'oeil. Avec un autre nombre
   de paliers, l'echelle de FormatDiag, le jaune pale remplace par l'orange. */
export const PALIERS_BILAN_TROIS = ['#B23A3A', '#E26438', '#91CF60']
export const COULEURS_REMPLACEES_BILAN = { '#FFFFBF': '#E26438' }
export const SEUILS_PAR_DEFAUT = [66, 33, 0]
export const SEGMENTS_JAUGE = 20
export const jaugeDuScore = (score, seuils = SEUILS_PAR_DEFAUT) => {
  const s = nombre(score) ?? 0
  const paliers = (Array.isArray(seuils) && seuils.length ? seuils : SEUILS_PAR_DEFAUT).map(Number)
  let iPalier = paliers.findIndex((seuil) => s >= seuil)
  if (iPalier < 0) iPalier = paliers.length - 1
  /* les paliers vont du plus haut (indice 0) au plus bas */
  const diag = couleurDuPalier(iPalier, paliers.length)
  const couleur = paliers.length === 3
    ? PALIERS_BILAN_TROIS[2 - iPalier]
    : (COULEURS_REMPLACEES_BILAN[String(diag).toUpperCase()] || diag)
  return {
    remplis: Math.max(0, Math.min(SEGMENTS_JAUGE, Math.round((s / 100) * SEGMENTS_JAUGE))),
    couleur,
  }
}

/* ----------------------------------------------------- le choix des textes
   - 3 ATOUTS : les trois scores les plus hauts ;
   - A CONSOLIDER (« Ta prochaine marche ») : le plus bas des autres ;
   - a egalite, l'ordre des fiches dans la bibliotheque (connu d'avance,
     jamais le hasard) ;
   - POUR TON CV : la premiere des trois phrases de chaque atout ;
   - multitache et autonomie : ecartees d'office (voir plus haut).
   Une competence sans fiche complete pour ce public ne peut pas aller sur la
   page : elle est ecartee. Etape 2 : il faut AU MOINS 3 competences ; avec
   3 exactement, pas de competence a consolider (`aConsolider` null, pas de
   « Ta prochaine marche »). Moins de 3 : pas de bilan (null).
   `seuils` (facultatif) : les seuils du Diagnostic par competence, pour la
   couleur de la jauge. */
export const choisirContenuBilan = ({ scores, bibliotheque, public: pub = 'etudiant', seuils = {} }) => {
  const p = PUBLICS.includes(pub) ? pub : 'etudiant'
  const rang = (f) => bibliotheque.fiches.indexOf(f)
  const vues = new Set()
  const candidats = []
  Object.entries(scores || {}).forEach(([cle, v]) => {
    const score = nombre(v)
    const fiche = trouverFiche(bibliotheque, cle)
    if (score === null || estRetiree(cle) || !fiche || vues.has(fiche) || manquesDeLaFiche(fiche, p).length) return
    vues.add(fiche)
    candidats.push({ cle, score, fiche })
  })
  if (candidats.length < 3) return null
  candidats.sort((a, b) => (b.score - a.score) || (rang(a.fiche) - rang(b.fiche)))
  const atouts = candidats.slice(0, 3)
  const reste = candidats.slice(3)
  const plusBas = reste.length ? Math.min(...reste.map((c) => c.score)) : null
  const aConsolider = reste.length ? reste.filter((c) => c.score === plusBas).sort((a, b) => rang(a.fiche) - rang(b.fiche))[0] : null
  const seuilsDe = (cle) => seuils?.[cle] ?? seuils?.[aplatirCle(cle)]
  return {
    public: p,
    atouts: atouts.map(({ cle, score, fiche }) => ({
      cle,
      nomUsage: fiche.nom_usage,
      nomCoreSkills: fiche.nom_coreskills,
      enSituation: fiche.publics[p].enSituation,
      amorce: fiche.publics[p].amorce,
      phraseCv: fiche.publics[p].phrasesCv[0],
      jauge: jaugeDuScore(score, seuilsDe(cle)),
    })),
    aConsolider: aConsolider ? {
      cle: aConsolider.cle,
      nomUsage: aConsolider.fiche.nom_usage,
      nomCoreSkills: aConsolider.fiche.nom_coreskills,
      conseil: aConsolider.fiche.publics[p].conseil,
      jauge: jaugeDuScore(aConsolider.score, seuilsDe(aConsolider.cle)),
    } : null,
    provisoire: {
      atouts: atouts.some((c) => c.fiche.provisoire),
      aConsolider: aConsolider?.fiche?.provisoire === true,
      cv: atouts.some((c) => c.fiche.provisoire),
    },
  }
}

/* ------------------------------------------ quand le bilan est-il disponible ?
   Proposition (a valider par Nawal) : l'icebreaker fait et TOUS les modules du
   plan termines, au sens du certificat (isDone et 100 %, moduleTermine). */
export const parcoursTermine = (actPlan) => Array.isArray(actPlan)
  && actPlan.length > 1
  && actPlan[0]?.isDone === true
  && actPlan.slice(1).every((m) => moduleTermine(m))
